
import React, { useEffect } from 'react';
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Zap, Trophy, Quote, Check } from 'lucide-react';
import logo2 from '../assets/logo2.png';
import ricardo from '../assets/ricardo.png';
import ana from '../assets/ana.png';
import lucas from '../assets/lucas.png';

const LandingPageV1 = ({ onGetStarted }) => {
    useEffect(() => {
        document.documentElement.classList.remove('dark');
        return () => {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'dark') {
                document.documentElement.classList.add('dark');
            }
        };
    }, []);

    const scrollToPricing = () => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSubscribe = async (priceId) => {
        try {
            // Use absolute URL to be safe, or relative if you prefer. 
            // Relying on the logic from existing LandingPage.jsx
            const apiBaseUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiBaseUrl}/api/create-public-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    price_id: priceId,
                    cancel_url: window.location.href
                })
            });

            const { url } = await response.json();
            if (url) window.location.href = url;
            else alert('Erro ao iniciar checkout. Tente novamente.');
        } catch (err) {
            console.error("Payment error", err);
            alert("Erro ao conectar com o servidor.");
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20">
            {/* Hero Section */}
            <section className="relative pt-20 pb-32 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background -z-10" />
                <div className="max-w-4xl mx-auto text-center space-y-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 text-destructive text-sm font-medium animate-fade-in-up">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Pare de se enganar</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70 leading-tight">
                        "Amanhã eu faço"
                    </h1>

                    <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-medium">
                        A frase que está custando seus sonhos, sua carreira e sua autoestima.
                    </p>

                    <div className="pt-8">
                        <button
                            onClick={scrollToPricing}
                            className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-foreground bg-primary rounded-full hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-primary/25 hover:-translate-y-1"
                        >
                            Começar Agora e Não Deixar pra Depois
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <p className="mt-4 text-sm text-muted-foreground">Experimente por 30 dias sem risco</p>
                    </div>
                </div>
            </section>

            {/* The Problem (Agitation) */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold mb-8 text-center">Você conhece essa sensação?</h2>
                    <div className="prose dark:prose-invert max-w-none text-lg leading-relaxed text-muted-foreground">
                        <p className="mb-6">
                            É segunda-feira de manhã. Você acorda já sabendo o que precisa fazer. Aquele projeto importante. Aquele curso que você comprou há 3 meses. Aquela conversa difícil que você vem adiando.
                        </p>
                        <p className="mb-6">
                            E aí você pensa: <span className="italic text-foreground">"Hoje não... amanhã eu começo direito."</span>
                        </p>
                        <div className="p-6 bg-background rounded-2xl border border-border shadow-sm my-8">
                            <p className="font-semibold text-foreground text-xl">
                                Só que amanhã vira semana que vem. Semana que vem vira mês que vem. E quando você percebe, mais um ano passou.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Hard Truth */}
            <section className="py-20">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold mb-12 text-center md:text-4xl">A verdade que dói (mas precisa ser dita)</h2>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6">
                            <p className="text-lg text-muted-foreground">
                                Não é preguiça. Não é falta de capacidade.
                            </p>
                            <p className="text-lg text-muted-foreground">
                                Você sabe exatamente o que precisa fazer. Você até quer fazer. Mas entre o querer e o fazer, tem um abismo chamado <strong className="text-foreground">procrastinação</strong>.
                            </p>
                        </div>

                        <div className="bg-destructive/5 border border-destructive/20 rounded-2xl p-8">
                            <h3 className="text-xl font-semibold mb-6 text-destructive flex items-center gap-2">
                                <Zap className="w-5 h-5" />
                                O que esse abismo está engolindo:
                            </h3>
                            <ul className="space-y-4">
                                {[
                                    "Aquela promoção que você merecia",
                                    "O corpo que você queria ter",
                                    "O negócio que você sonha em começar",
                                    "A paz de espírito de estar com tudo em dia",
                                    "A confiança de ser alguém que cumpre o que promete"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-destructive/60 shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <p className="text-2xl font-semibold text-center mt-12 max-w-2xl mx-auto">
                        A procrastinação não é um problema de produtividade. <br className="hidden md:block" />
                        <span className="text-primary">É um problema de vida.</span>
                    </p>
                </div>
            </section>

            {/* The Strategy */}
            <section className="py-20 bg-primary/5">
                <div className="container mx-auto px-4 max-w-4xl text-center">
                    <h2 className="text-3xl font-bold mb-6">Por que você continua adiando?</h2>
                    <p className="text-xl text-muted-foreground mb-12">
                        Porque ninguém te ensinou a <strong className="text-foreground">ação antes da motivação</strong>.
                    </p>

                    <div className="bg-background rounded-3xl p-8 md:p-12 shadow-xl border border-border">
                        <div className="grid md:grid-cols-2 gap-8 items-center text-left">
                            <div>
                                <h3 className="text-xl font-bold mb-4">O Mito da Motivação</h3>
                                <p className="text-muted-foreground">
                                    A gente cresce acreditando que precisa estar "a fim", "inspirado" ou "no mood certo" para fazer as coisas. <span className="font-bold text-destructive">Mentira.</span>
                                </p>
                            </div>
                            <div className="relative pl-8 border-l-4 border-primary/20">
                                <p className="text-lg font-medium italic">
                                    "As pessoas que fazem acontecer não esperam a motivação chegar. Elas criam sistemas que tornam impossível não agir."
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Introducing Solution */}
            <section className="py-24 overflow-hidden relative">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="text-center mb-16">
                        <span className="text-primary font-semibold tracking-wider text-sm uppercase">Apresentando</span>
                        <h2 className="text-4xl md:text-5xl font-black mt-2 mb-6">SkipLater</h2>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            O método que transforma procrastinadores em realizadores.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="bg-muted/50 p-8 rounded-3xl border border-border hover:border-primary/50 transition-colors">
                            <div className="p-3 bg-primary/10 rounded-2xl w-fit mb-6">
                                <Clock className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">1. Redução de Fricção</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Quebramos suas tarefas em passos tão pequenos que fica constrangedor não fazer. Não começa escrevendo o livro — começa abrindo o documento.
                            </p>
                        </div>

                        <div className="bg-muted/50 p-8 rounded-3xl border border-border hover:border-primary/50 transition-colors">
                            <div className="p-3 bg-primary/10 rounded-2xl w-fit mb-6">
                                <Zap className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">2. Pressão Saudável</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Compromissos sociais e recompensas imediatas que te fazem querer começar de verdade. Seu cérebro vai aprender que fazer dói menos que adiar.
                            </p>
                        </div>

                        <div className="bg-muted/50 p-8 rounded-3xl border border-border hover:border-primary/50 transition-colors">
                            <div className="p-3 bg-primary/10 rounded-2xl w-fit mb-6">
                                <Trophy className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">3. Celebração de Micro-vitórias</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Cada pequena ação é uma vitória. E vitórias criam momentum. Momentum cria identidade. Identidade traz resultados.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it Works - Timeline */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-center mb-16">Como funciona na prática?</h2>

                    <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-primary/20 before:to-transparent">
                        {[
                            {
                                title: "Passo 1",
                                text: "Você escolhe aquela tarefa que está te perseguindo há semanas (ou meses)."
                            },
                            {
                                title: "Passo 2",
                                text: "O SkipLater quebra ela em micro-ações de 5 minutos que você pode fazer AGORA."
                            },
                            {
                                title: "Passo 3",
                                text: "Você faz a primeira ação e sente o alívio de finalmente ter começado."
                            },
                            {
                                title: "Passo 4",
                                text: 'O sistema te guia pela próxima ação, até que você olhe pra trás e pense: "Caramba, eu realmente fiz."'
                            }
                        ].map((step, idx) => (
                            <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-background bg-background shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 text-xs font-bold">
                                    {idx + 1}
                                </div>
                                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-background p-6 rounded-2xl border border-border shadow-sm">
                                    <span className="font-bold text-primary block mb-1">{step.title}</span>
                                    <p className="text-muted-foreground">{step.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Checklist */}
            <section className="py-20">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="text-3xl font-bold mb-10 text-center">O que você vai conseguir:</h2>
                    <div className="space-y-6">
                        {[
                            "Acabar com a culpa diária de saber que deveria estar fazendo algo e não está",
                            "Reconquistar sua autoconfiança e provar que você cumpre o que promete",
                            "Realizar em 30 dias o que você vem adiando há 6 meses",
                            "Desenvolver o hábito da ação imediata que separa quem sonha de quem faz",
                            "Sentir aquela paz de dormir sabendo que você avançou"
                        ].map((benefit, i) => (
                            <div key={i} className="flex gap-4 p-4 rounded-xl hover:bg-accent/50 transition-colors">
                                <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0" />
                                <span className="text-lg text-foreground/90 font-medium">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-bold text-center mb-16">Depoimentos de quem parou de adiar</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                quote: "Eu tinha um projeto de e-book parado há 8 meses. Com o SkipLater, terminei em 3 semanas. Não foi mágica — foi ter alguém me mostrando o próximo passo minúsculo.",
                                author: "Marina, 34 anos",
                                img: ana
                            },
                            {
                                quote: "Sempre me achei preguiçoso. Descobri que não sou preguiçoso, só estava usando o método errado. Hoje eu faço em 1 hora o que antes eu adiava por semanas.",
                                author: "Roberto, 28 anos",
                                img: ricardo
                            },
                            {
                                quote: "Pela primeira vez na vida eu não to com aquele peso nas costas de mil coisas pendentes. Mudou minha ansiedade completamente.",
                                author: "Lucas, 41 anos",
                                img: lucas
                            }
                        ].map((testimonial, i) => (
                            <div key={i} className="bg-background p-8 rounded-3xl border border-border shadow-sm relative flex flex-col">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/20 shrink-0">
                                        <img src={testimonial.img} alt={testimonial.author.split(',')[0]} className="w-full h-full object-cover" />
                                    </div>
                                    <p className="font-bold text-foreground text-sm">{testimonial.author}</p>
                                </div>
                                <Quote className="w-8 h-8 text-primary/20 absolute top-8 right-8" />
                                <p className="text-muted-foreground relative italic text-lg">"{testimonial.quote}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section (Replaces the "Final Pitch" CTA partially, or sits below it) */}
            {/* Integrating the Final Pitch AND Pricing together for maximum effect */}
            <section id="pricing" className="py-24 px-4 relative overflow-hidden bg-background">
                <div className="absolute inset-0 bg-primary/5 -z-10" />
                <div className="max-w-4xl mx-auto text-center mb-12">
                    <h2 className="text-3xl md:text-5xl font-bold mb-8">Quanto custa sua procrastinação?</h2>
                    <div className="prose dark:prose-invert mx-auto mb-10 text-lg text-muted-foreground">
                        <p>O SkipLater custa menos que um cafézinho por semana. É o investimento mais barato que você vai fazer para recuperar o controle da sua vida.</p>
                    </div>
                </div>

                <div className="container px-4 mx-auto max-w-5xl">
                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
                        {/* Monthly Plan */}
                        <div className="relative rounded-3xl border-2 p-8 flex flex-col transition-all duration-300 border-border bg-background/80 backdrop-blur-sm hover:border-primary/50">
                            <h3 className="text-xl font-bold mb-2">Plano Mensal</h3>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">R$ 9,90</span>
                                <span className="text-muted-foreground text-sm font-medium">/mês</span>
                            </div>
                            <ul className="space-y-3 mb-8 flex-1">
                                {["Acesso completo ao sistema", "Atualizações constantes", "Cancele quando quiser"].map((text, i) => (
                                    <li key={i} className="flex gap-3 text-sm">
                                        <Check className="w-5 h-5 text-primary shrink-0" />
                                        <span>{text}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)}
                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-12 px-4 w-full text-lg rounded-xl font-bold"
                            >
                                Assinar Mensal
                            </button>
                            <p className="text-xs text-center mt-3 text-muted-foreground">Sem fidelidade.</p>

                        </div>

                        {/* Yearly Plan */}
                        <div className="relative rounded-3xl border-2 p-8 flex flex-col transition-all duration-300 border-primary bg-background shadow-2xl scale-105 z-10">
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">Recomendado</div>
                            <h3 className="text-xl font-bold mb-2">Plano Anual</h3>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">R$ 97</span>
                                <span className="text-muted-foreground text-sm font-medium">/ano</span>
                            </div>
                            <p className="text-sm text-green-600 font-bold mb-4 bg-green-50 w-fit px-3 py-1 rounded-full">Economize 20%</p>
                            <ul className="space-y-3 mb-8 flex-1">
                                {["Melhor custo-benefício", "Menos de R$ 0,27 por dia", "Compromisso real com sua mudança", "Garantia incondicional de 30 dias"].map((text, i) => (
                                    <li key={i} className="flex gap-3 text-sm">
                                        <Check className="w-5 h-5 text-primary shrink-0" />
                                        <span>{text}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_YEARLY)}
                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] h-14 px-4 w-full text-lg rounded-xl font-bold"
                            >
                                Assinar Anual (R$ 97)
                            </button>
                            <p className="text-xs text-center mt-3 text-muted-foreground">30 dias de garantia. Risco zero.</p>
                        </div>
                    </div>

                    <div className="mt-16 text-center max-w-2xl mx-auto">
                        <div className="bg-background/50 rounded-xl p-6 border border-primary/20">
                            <h3 className="font-bold text-lg mb-2">Garantia Anti-Arrependimento</h3>
                            <p className="text-muted-foreground text-sm">Teste o SkipLater por 30 dias completos. Se você não sentir que finalmente está saindo da paralisia, a gente devolve 100% do seu dinheiro. Sem perguntas.</p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 text-left mt-16 max-w-3xl mx-auto opacity-75">
                        <div>
                            <h4 className="font-bold mb-2">Opção 1:</h4>
                            <p className="text-sm text-muted-foreground">Você fecha essa página e continua prometendo que "amanhã começa".</p>
                        </div>
                        <div>
                            <h4 className="font-bold mb-2">Opção 2:</h4>
                            <p className="text-sm text-muted-foreground">Você investe menos de 30 centavos por dia e resolve esse problema de uma vez.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-background border-t border-border/50">
                <div className="container px-4 mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <img src={logo2} alt="SkipLater" className="h-8 w-auto rounded-lg grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" />
                        <span className="text-sm text-muted-foreground font-medium">SkipLater © 2025</span>
                    </div>

                    <div className="flex gap-6 text-sm text-muted-foreground">
                        <a href="/privacy" className="hover:text-foreground transition-colors">Politica de Privacidade</a>
                        <a href="/terms" className="hover:text-foreground transition-colors">Termos de Uso</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPageV1;
